/**
 * File Upload API Service
 * R2 File Upload to /library/upload endpoint
 */

import { apiClient } from "@/api/config/api-client";
import { API_ENDPOINTS } from "@/api/config/api-endpoints";
import { getFileCategory } from "../utils/file-utils";
import { getFileUrl } from "@/lib/utils";

/**
 * Upload request parameters
 */
export interface UploadFileParams {
  file: File;
  title?: string; // Optional, auto-generated from filename if not provided
  isInput?: boolean; // true for generation inputs, false/undefined for user library
  isWardrobe?: boolean; // true for wardrobe uploads
  tags?: string[]; // e.g. ["user"] | ["product"]
}

/**
 * R2 File object from backend
 */
export interface R2FileObject {
  type: string; // mime type (e.g., "image/png")
  size: number;
  folder: string;
  fileUrl: string; // R2 relative path (without base URL)
}

/**
 * Upload response from backend
 */
export interface UploadFileResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    _id: string;
    userId: string;
    title: string;
    type: "image" | "video" | "audio" | "document"; // File category
    file: R2FileObject; // Nested R2 file object
    isInput?: boolean;
    tags?: string[];
    createdAt: string;
    updatedAt: string;
    __v: number;
  };
}

/**
 * Upload file to R2 via /library/upload endpoint
 * @param params - Upload parameters
 * @returns Upload response with R2 file URL
 */
export const uploadFile = async (
  params: UploadFileParams
): Promise<UploadFileResponse> => {
  const { file, title, isInput, isWardrobe, tags } = params;
  const validTags = (tags || [])
    .map((tag) => tag?.trim())
    .filter((tag): tag is string => tag === "user" || tag === "product");

  // ✅ Check if file already has a fileUrl (from file picker/library)
  const existingFileUrl = (file as any).fileUrl;
  if (existingFileUrl) {
    // File already exists in R2, return mock response with existing URL
    const mockResponse: UploadFileResponse = {
      statusCode: 200,
      success: true,
      message: "File already exists in R2",
      data: {
        _id: `existing_${Date.now()}`,
        userId: "existing",
        title: title || file.name,
        type: getFileCategory(file.type) as any,
        file: {
          type: file.type,
          size: file.size,
          folder: "chatharmony",
          fileUrl: existingFileUrl.replace(/^https?:\/\/[^\/]+/, ""), // Remove base URL, keep only path
        },
        isInput: isInput || false,
        tags: validTags,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        __v: 0,
      },
    };
    return mockResponse;
  }

  // ✅ Determine file category (image, video, audio, document)
  const fileCategory = getFileCategory(file.type);
  if (fileCategory === "unknown") {
    throw new Error(`Desteklenmeyen dosya tipi: ${file.type}`);
  }

  // ✅ Create FormData
  const formData = new FormData();
  formData.append("file", file);

  // ✅ Add title if provided
  if (title) {
    formData.append("title", title);
  }

  // ✅ Add file category (NOT mime type!)
  formData.append("type", fileCategory);

  // ✅ Add isInput flag if true (for generation inputs)
  if (isInput === true) {
    formData.append("isInput", "true");
  }

  // ✅ Add isWardrobe flag if true (for wardrobe uploads)
  if (isWardrobe === true) {
    formData.append("isWardrobe", "true");
  }

  // ✅ Add tags if provided
  if (validTags.length > 0) {
    validTags.forEach((tag) => {
      formData.append("tags[]", tag);
    });
  }

  // ✅ Upload to main API (VITE_API_BASE_URL)
  const response = await apiClient.post<UploadFileResponse>(
    API_ENDPOINTS.uploadFile,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  // ✅ ApiClient.post already returns T (not AxiosResponse<T>)
  return response;
};

/**
 * Upload multiple files (batch upload)
 * @param files - Array of upload parameters
 * @returns Array of upload responses (only successful uploads)
 */
export const uploadMultipleFiles = async (
  files: UploadFileParams[]
): Promise<UploadFileResponse[]> => {
  // ✅ Upload files sequentially (to avoid overwhelming the server)
  const results: UploadFileResponse[] = [];

  for (const fileParams of files) {
    try {
      const result = await uploadFile(fileParams);
      results.push(result);
    } catch (errorResponse: any) {
      console.error("File upload failed:", fileParams.file.name, errorResponse);
      // Continue with other files even if one fails
    }
  }

  return results;
};

/**
 * Get full URL from upload response
 * @param response - Upload response from backend
 * @returns Full R2 URL
 */
export const getUploadedFileUrl = (response: UploadFileResponse): string => {
  const url = getFileUrl(response.data.file);
  if (!url) {
    throw new Error("Failed to generate file URL from response");
  }
  return url;
};

/**
 * Check if URL is from our R2 storage
 * @param url - URL to check
 * @returns true if URL is from our R2 storage
 */
export const isR2Url = (url: string): boolean => {
  if (!url) return false;

  const r2BaseUrl = import.meta.env.VITE_R2_PUBLIC_BASE_URL;
  if (!r2BaseUrl) return false;

  return url.startsWith(r2BaseUrl);
};

/**
 * Helper to guess file type from URL extension
 */
const guessTypeFromUrl = (url: string): string => {
  try {
    const pathname = new URL(url).pathname;
    const ext = pathname.split(".").pop()?.toLowerCase();
    if (!ext) return "image"; // Default fallback

    // Simple mapping based on common extensions
    if (["jpg", "jpeg", "png", "gif", "webp", "svg", "avif"].includes(ext))
      return "image";
    if (["mp4", "webm", "mov", "avi", "mkv"].includes(ext)) return "video";
    if (["mp3", "wav", "ogg", "m4a"].includes(ext)) return "audio";
    if (["pdf", "doc", "docx", "xls", "xlsx", "txt"].includes(ext))
      return "document";

    return "image"; // Default
  } catch {
    return "image";
  }
};

/**
 * Upload file from external URL to R2 via backend
 * @param params - Upload parameters
 * @returns Upload response with new R2 URL
 */
export const uploadFromUrl = async (params: {
  url: string;
  type: string;
  title?: string;
  isInput?: boolean;
  isWardrobe?: boolean;
  tags?: string[];
}): Promise<UploadFileResponse> => {
  const { url, type, title, isInput, isWardrobe, tags } = params;
  const validTags = (tags || [])
    .map((tag) => tag?.trim())
    .filter((tag): tag is string => tag === "user" || tag === "product");

  // ✅ Check if already R2 URL
  if (isR2Url(url)) {
    throw new Error("URL is already from R2 storage");
  }

  const payload = {
    url,
    type,
    title: title || url.split("/").pop() || "URL File",
    isInput: isInput || false,
    isWardrobe: isWardrobe || false,
    tags: validTags.length > 0 ? validTags : undefined,
  };

  try {
    const response = await apiClient.post<UploadFileResponse>(
      API_ENDPOINTS.uploadFromUrl,
      payload
    );
    return response;
  } catch (error: any) {
    throw new Error(`Failed to upload from URL: ${error.message}`);
  }
};

/**
 * Process URLs: Upload external URLs to R2, keep R2 URLs as-is
 * @param urls - Array of URLs (may contain external or R2 URLs)
 * @param options - Upload options
 * @returns Array of R2 URLs (all normalized to R2)
 */
export const processUrls = async (
  urls: string[],
  options: Partial<UploadFileParams> = {}
): Promise<string[]> => {
  const processedUrls: string[] = [];

  for (const url of urls) {
    try {
      if (isR2Url(url)) {
        // ✅ Already R2 URL, keep as-is
        processedUrls.push(url);
      } else {
        // ✅ External URL, upload to R2
        const type = guessTypeFromUrl(url);
        const response = await uploadFromUrl({
          url,
          type,
          title: options.title,
          isInput: options.isInput,
          isWardrobe: options.isWardrobe,
          tags: options.tags,
        });
        const r2Url = getUploadedFileUrl(response);
        processedUrls.push(r2Url);
      }
    } catch (error: any) {
      console.error(`Failed to process URL: ${url}`, error);
      // ✅ Keep original URL on error (fallback)
      processedUrls.push(url);
    }
  }

  return processedUrls;
};

/**
 * Process File objects: Upload new files to R2, keep existing R2 files as-is
 * @param files - Array of File objects (may contain new files or existing R2 files)
 * @param options - Upload options
 * @returns Array of R2 URLs (all normalized to R2)
 */
export const processFiles = async (
  files: File[],
  options: Partial<UploadFileParams> = {}
): Promise<string[]> => {
  const processedUrls: string[] = [];

  for (const file of files) {
    try {
      // Check if file already has a fileUrl (from file picker/library)
      const existingFileUrl = (file as any).fileUrl;
      if (existingFileUrl) {
        // ✅ File already exists in R2, use existing URL
        processedUrls.push(existingFileUrl);
      } else {
        // ✅ New file, upload to R2
        const response = await uploadFile({
          file,
          ...options,
        });
        const r2Url = getUploadedFileUrl(response);
        processedUrls.push(r2Url);
      }
    } catch (error: any) {
      console.error(`Failed to process file: ${file.name}`, error);
      // ✅ Skip failed files
    }
  }

  return processedUrls;
};
